import axios from "axios";

/**
 * Thin axios wrapper for the campaign co-ownership endpoints. Same shape as
 * bdcApi.js / drApi.js: callers get plain promises resolving to response.data.
 *
 * ⚠ These routes are NOT under /admin/*. They are mounted at the API root with
 * `verifyToken`, and an admin token works on them unchanged: loginAdmin signs
 * the identical {id} claim with the same TOKEN_KEY as the portal login, so
 * verifyToken admits it, and assertCampaignAccess reads only req.userId before
 * falling through to the admin bypass (campaignAccess.js:174-193). No /admin/*
 * twin is needed, and adding one would put a second door on the same handlers
 * for no gain.
 *
 * ⚠ If a twin IS ever added it must carry the route-level marker in the SAME
 * commit — verifyAdminToken never sets req.authUser, which is the trap that has
 * already cost this programme a silent admin 403 more than once.
 *
 * Deliberately NOT wrapped here: mint and revoke. Both sit behind the
 * `collab_enabled` feature gate, whose migration seeds it false and which
 * production has never run — so they answer 503 there. A staff button that
 * cannot work is worse than an absent one; see the panel's docblock.
 */

function apiBase() {
  const raw = process.env.REACT_APP_API || "";
  return raw.endsWith("/") ? raw : raw + "/";
}

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  return token ? { "x-access-token": token } : {};
}

function url(path) {
  const clean = path.startsWith("/") ? path.slice(1) : path;
  return apiBase() + clean;
}

export const collabApi = {
  /** ACTIVE co-owners, including a synthesized row for an unseated creator. */
  listParticipants: async (campaignId) => {
    const res = await axios.get(url("campaign-participants"), {
      headers: authHeaders(),
      params: { campaign_id: campaignId },
    });
    return res.data;
  },

  /** PENDING invitations only — the API hard-filters, so there is no history. */
  listInvitations: async (campaignId) => {
    const res = await axios.get(url("campaign-invitations"), {
      headers: authHeaders(),
      params: { campaign_id: campaignId },
    });
    return res.data;
  },

  /**
   * Soft-end an ACTIVE participation. Ungated on purpose (A40): the kill switch
   * must not disable its own remedy. Refused with 409 CREATOR_PROTECTED for the
   * campaign creator (A25 — unconditional, admins included) and 409 LAST_OWNER
   * when it would leave the campaign with no owner.
   */
  removeParticipant: async (campaignId, userId) => {
    const res = await axios.post(
      url("campaign-participants/remove"),
      { campaign_id: campaignId, user_id: userId },
      { headers: authHeaders() }
    );
    return res.data;
  },
};

export default collabApi;
